import { Endpoint } from '@business/api/endpoint';
import { CompactUserResponse, UserResponse } from '@business/entities/account';
import { CommonService } from '@business/services';
import { FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import MFormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import JoditEditor, { Jodit } from 'jodit-pro-react';
import React, { useCallback, useMemo, useRef, Fragment, useState, useEffect } from 'react';
import { FormGroup, InputProps, Label, UncontrolledTooltip } from 'reactstrap';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FormStringInputProps } from './FormInput';
import './form_html.scss';
import { ProjectUserResponse } from '@business/entities/project';
import { RTEUploadResponse } from '@business/entities/common';
import { useLazyEffect } from '@core/utils/react';
import { openAssistantModal } from '@modules/common/Assistant';
import { HtmlListConsolidator } from '@core/utils/HtmlListConsolidator';
import { CannedResponse } from '@business/entities/canned';
type IJodit = any;

function getButtons(
  canned: boolean,
  media: boolean,
  minimal: boolean,
  veryMinimal: boolean,
  cannedName: string,
  openMediaModal: any,
  openCannedModal: any,
  hasList: boolean,
  isAgentActivated: boolean,
  openAssistant: any,
) {
  const agent = isAgentActivated
    ? [
        {
          name: 'Generate with AI',
          exec: (editor: IJodit) => {
            openAssistant(editor as IJodit, editor.s.html);
            return false;
          },
        },
      ]
    : [];
  if (veryMinimal) {
    return [
      {
        group: 'font-style',
        buttons: [],
      },
      {
        group: 'list',
        buttons: [],
      },
      {
        group: 'indent',
        buttons: [],
      },
      {
        group: 'font',
        buttons: [],
      },
      {
        group: 'color',
        buttons: [],
      },
      {
        group: 'media',
        buttons: [],
      },
      {
        group: 'insert',
        buttons: [],
      },
      {
        group: 'form',
        buttons: [],
      },
      {
        group: 'other',
        buttons: [],
      },
      {
        group: 'info',
        buttons: [],
      },
    ];
  }

  if (minimal) {
    return [
      {
        group: 'font-style',
        buttons: [],
      },
      {
        group: 'list',
        buttons: [],
      },
      {
        group: 'indent',
        buttons: [],
      },
      {
        group: 'font',
        buttons: [],
      },
      {
        group: 'color',
        buttons: [],
      },
      {
        group: 'media',
        buttons: [],
      },
      {
        group: 'insert',
        buttons: [],
      },
      {
        group: 'form',
        buttons: [],
      },
      {
        group: 'other',
        buttons: [],
      },
      ...(canned
        ? [
            {
              group: 'info',
              buttons: [],
            },
            {
              name: cannedName,
              exec: (editor: IJodit) => {
                openCannedModal(editor as IJodit, editor.s.html);
                return false;
              },
            },
          ]
        : [
            {
              group: 'info',
              buttons: [],
            },
          ]),
      ...agent,
    ];
  }
  return [
    {
      group: 'source',
      buttons: [],
    },
    {
      group: 'font-style',
      buttons: [],
    },
    {
      group: 'list',
      buttons: [],
    },
    {
      group: 'indent',
      buttons: [],
    },
    {
      group: 'font',
      buttons: [],
    },
    {
      group: 'color',
      buttons: [],
    },
    {
      group: 'media',
      buttons: [],
    },
    '\n',
    {
      group: 'state',
      buttons: [],
    },
    {
      group: 'clipboard',
      buttons: [],
    },
    {
      group: 'insert',
      buttons: [],
    },
    {
      group: 'form',
      buttons: [],
    },
    {
      group: 'history',
      buttons: [],
    },
    {
      group: 'search',
      buttons: [],
    },
    {
      group: 'other',
      buttons: [],
    },
    {
      group: 'others',
      buttons: hasList ? ['variables'] : [],
    },

    ...(canned
      ? [
          {
            group: 'info',
            buttons: [],
          },
          {
            name: cannedName,
            exec: (editor: IJodit) => {
              openCannedModal(editor as IJodit, editor.s.html);
              return false;
            },
          },
        ]
      : [
          {
            group: 'info',
            buttons: [],
          },
        ]),
    ...agent,
    ...(media
      ? [
          {
            name: 'Media Links',
            exec: (editor: IJodit) => {
              openMediaModal(editor as IJodit, editor.s.html);
              return false;
            },
          },
        ]
      : [
          {
            group: 'info',
            buttons: [],
          },
        ]),
  ];
}

export type FormInputProps<C extends FormControl<any>> = FormStringInputProps<C>;
export const __editors: Record<string, IJodit> = {};

export function FormHTML<C extends FormControl<any>>(
  props: FormInputProps<C> &
    InputProps & {
      minimal?: boolean;
      veryMinimal?: boolean;
      full?: boolean;
      variables?: any;
      inKey?: string;
      theme?: string;
      noMinimumHeight?: boolean;
      minHeight?: number;
      maxHeight?: number;
      height?: number;
      canned?: (text?: string) => Promise<Observable<CannedResponse>>;
      cannedName?: string;
      media?: (text?: string) => Promise<Observable<string>>;
      users?: (ProjectUserResponse | CompactUserResponse | UserResponse)[];
      autoFocus?: boolean;
      askBeforePasteHTML?: boolean;
      spellcheck?: boolean;
      description?: string;
      statusBar?: boolean;
      onBlur?: () => void;
      onAgent?: () => Observable<string>;
      note?: React.ReactNode | string;
    },
) {
  const {
    minimal = false,
    veryMinimal = false,
    full = false,
    canned,
    media,
    cannedName = 'Canned Responses',
    autoFocus = false,
    noMinimumHeight = false,
    statusBar = true,
    askBeforePasteHTML = CommonService.isTenants(['1649106174']),
    onAgent,
  } = props;
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldValue = R.toString(fieldState.value) || '';

  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;

  const fieldPlaceholder = fieldConfig.placeholder || '';
  const [initialValue, setInitialValue] = useState(fieldValue);

  const [isFocus, setIsFocused] = useState(false);

  useEffect(() => {
    if (initialValue !== fieldValue && !isFocus) {
      setInitialValue(fieldValue);
    }
  }, [initialValue, fieldValue, isFocus]);

  const handleChangeText = useCallback(
    (value: string) => {
      // Apply list consolidation to fix formatting issues
      const consolidatedValue = HtmlListConsolidator.consolidateLists(HtmlListConsolidator.normalizeListItems(value));
      formControl.setValue(consolidatedValue);
    },
    [formControl],
  );

  const openCannedModal = useCallback(
    (editor: IJodit, initialHTML: string) => {
      editor.selection.save(true);

      canned!(R.isEmpty(initialHTML) ? undefined : initialHTML).then(obs => {
        obs
          .pipe(
            tap(response => {
              setTimeout(() => {
                __editors[formControl.uid].selection.insertHTML(response?.content || response);
              }, 1000);
            }),
          )
          .subscribe();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const openAssistant = useCallback(
    (editor: IJodit, initialHTML: string) => {
      editor.selection.save(true);

      if (onAgent) {
        onAgent()
          .pipe(
            tap(response => {
              if (!R.isEmpty(response)) {
                setTimeout(() => {
                  __editors[formControl.uid].selection.insertHTML(response);
                }, 1000);
              }
            }),
          )
          .subscribe();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAgent],
  );

  const tooltip = useMemo(() => {
    if (!props.description) return null;
    const uid = formControl.uid.replace('form:', '');
    return (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip container={props.container} placement="top" target={`control-description-${uid}`}>
          {props.description}
        </UncontrolledTooltip>
      </Fragment>
    );
  }, [formControl.uid, props.container, props.description]);

  const openMediaModal = useCallback(
    (editor: IJodit, initialHTML: string) => {
      editor.selection.save(true);

      media!(R.isEmpty(initialHTML) ? undefined : initialHTML).then(obs => {
        obs
          .pipe(
            tap(text => {
              setTimeout(() => {
                __editors[formControl.uid].selection.insertHTML(text);
              }, 1000);
            }),
          )
          .subscribe();
      });
    },

    [formControl.uid, media],
  );

  const handleBlur = useCallback(() => {
    formControl.onBlur();
    props.onBlur && props.onBlur();
    setIsFocused(false);
  }, [formControl, props]);

  const handleFocus = useCallback(() => {
    formControl.onFocus();
    setIsFocused(true);
  }, [formControl]);

  const onMention = useCallback(
    (query: string) => {
      const searchQuery = R.trim(query);
      if (!searchQuery.startsWith('@')) return [];
      if (!props.users) return [];

      return props.users
        .filter(item => CommonService.getFullName(item).toLowerCase().includes(searchQuery.toLocaleLowerCase().substring(1)))
        .map(user => ({ title: `${CommonService.getFullName(user)}`, value: user.id, id: user.id, email: user.email }));
    },
    [props.users],
  );

  const joditHeight = useMemo(() => {
    return props.height || (noMinimumHeight ? null : 300);
  }, [noMinimumHeight, props.height]);

  const autoCompleteSettings = useMemo(() => {
    if (!props.users) return {};

    return {
      autocomplete: {
        sources: [
          {
            feed: onMention,
            insertValueRenderer: (item: any) =>
              `<span class="mention" style="color: #556ee6" data-mention="${item.id}" data-user-id="${item.id}" data-user-email="${item.email}">${item.title}</span>`,
          },
        ],
      },
    };
  }, [onMention, props.users]);

  const joditRef = useRef<Jodit | null>(null);

  // @ts-ignore
  const config: IJodit['options'] = useMemo(() => {
    const headers = Endpoint.dumpHeaders;
    delete headers['Content-Type'];
    delete headers['Accept'];

    return {
      ...Jodit.defaultOptions,
      license: 'M1NDC-1DHE1-C05P6-DPFOA',
      ...autoCompleteSettings,
      disablePlugins: [
        'button-generator',
        'google-maps',
        'google-search',
        'page-break',
        'finder',
        'backup',
        'highlight-signature',
        'emoji',
        'keyboard',
        'export-docs',
        'change-case',
        'show-blocks',
        'paste-code',
        'translate',
        'mobile-view',
      ],
      highlightSignature: {
        schema: {
          '\\{([^}]+)\\}': (jodit: Jodit) => {
            const color = '#FFFF0045';

            const span = jodit.createInside.element('span', {
              style: {
                backgroundColor: color,
              },
            });

            return span;
          },
        },
      },
      controls: {
        font: {
          list: Jodit.atom({
            '': 'Default',
            'Helvetica,sans-serif': 'Helvetica',
            'Arial,Helvetica,sans-serif': 'Arial',
            'Arial Black,sans-serif': 'Arial Black',
            'Comic Sans MS,Comic Sans,sans-serif': 'Comic Sans MS',
            'Courier New,Courier,monospace': 'Courier New',
            'Lucida Console,Monaco,monospace': 'Lucida Console',
            'Georgia,serif': 'Georgia',
            'Impact,Charcoal,sans-serif': 'Impact',
            'Tahoma,Geneva,sans-serif': 'Tahoma',
            'Times New Roman,Times,serif': 'Times New Roman',
            'Verdana,Geneva,sans-serif': 'Verdana',
          }),
        },
        variables: {
          name: 'Short Codes',
          list: props.variables || {},
          childTemplate: (editor: any, key: any, value: any) => {
            if (value.startsWith('h:')) {
              return `<span style="font-weight: bold; text-align: lefer" class="${key}">${editor.i18n(value.substring(2))}</span>`;
            }
            return `<span style="font-size: 12px; text-align: left" class="${key}">${editor.i18n(value)}</span>`;
          },
          exec: function (editor: any, _: any, { control }: any) {
            const value = control.args && control.args.length && control.args[0];
            if (value.startsWith('h:')) return false;
            if (value.startsWith('|')) return false;

            if (value) {
              (editor as Jodit).s.insertHTML(`{{${value}}}`);
            }
          },
        },
      },
      placeholder: fieldPlaceholder,
      ...(full ? { sizeLG: 0, sizeMD: 0, sizeSM: 0 } : {}),
      minHeight: props.minHeight || (noMinimumHeight ? null : 300),
      ...(joditHeight && { height: joditHeight }),
      maxHeight: props.maxHeight,
      readonly: fieldReadonly,
      allowResizeY: true,
      enableDragAndDropFileToEditor: true,
      saveModeInStorage: true,
      enter: 'div',
      uploader: {
        // ...Jodit.defaultOptions.uploader,
        headers: headers,
        url: Endpoint.dumpUrl('@gc/folder/rte-upload'),
        insertImageAsBase64URI: false,
        imagesExtensions: ['jpg', 'png', 'jpeg', 'gif'],
        format: 'json',
        defaultHandlerSuccess: function (data: RTEUploadResponse) {
          let i;
          if (data.files && data.files.length)
            if (data && data.files && data.files.length) {
              for (i = 0; i < data.files.length; i += 1) {
                if (R.get(data.isImages, i) === true) {
                  //   __editors[formControl.uid].selection.insertImage(data.baseurl + data.files[i], 250, 250, 'ss');
                  __editors[formControl.uid].selection.insertHTML(
                    `<img style="max-width: 100%" class="img-fluid" src="${data.baseurl + data.files[i]}" />`,
                  );
                } else {
                  const fileName = data.names[i];
                  const fileUrl = data.baseurl + data.files[i];

                  __editors[formControl.uid].selection.insertHTML(`<a class="rte-file-link" href="${fileUrl}" target="_blank">${fileName}</a>`);
                }
              }
            }
        },
      },
      askBeforePasteHTML: askBeforePasteHTML,
      defaultActionOnPaste: 'insert_clear_html',
      showToolip: true,
      removeButtons: minimal
        ? ['eraser', 'copyFormat', 'hr', 'print', 'preview', 'className', 'file', 'symbol', 'about', 'classSpan', 'table']
        : veryMinimal
        ? [
            'copyFormat',
            'hr',
            'print',
            'preview',
            'className',
            'symbol',
            'about',
            'classSpan',
            'table',
            'gif',
            'strikethrough',
            'eraser',
            'symbols',
            'fullsize',
          ]
        : ['copyformat', 'about', 'print', 'symbol', 'classSpan', 'superscript', 'subscript'],
      colorPickerDefaultTab: 'color',
      buttons: getButtons(
        !!canned,
        !!media,
        minimal,
        veryMinimal,
        cannedName,
        openMediaModal,
        openCannedModal,
        !!props.variables,
        !!onAgent,
        openAssistant,
      ),
      buttonsMD: getButtons(
        !!canned,
        !!media,
        minimal,
        veryMinimal,
        cannedName,
        openMediaModal,
        openCannedModal,
        !!props.variables,
        !!onAgent,
        openAssistant,
      ),
      buttonsSM: getButtons(
        !!canned,
        !!media,
        minimal,
        veryMinimal,
        cannedName,
        openMediaModal,
        openCannedModal,
        !!props.variables,
        !!onAgent,
        openAssistant,
      ),
      buttonsLG: getButtons(
        !!canned,
        !!media,
        minimal,
        veryMinimal,
        cannedName,
        openMediaModal,
        openCannedModal,
        !!props.variables,
        !!onAgent,
        openAssistant,
      ),
      theme: props.theme || 'gc',
      spellcheck: props.spellcheck,
      autofocus: autoFocus,
      ...(veryMinimal && {
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
      }),
      ...(!statusBar && {
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
      }),
      events: {
        afterInit(instance: any) {
          __editors[formControl.uid] = instance;
        },
        focus() {
          handleFocus();
        },
        click() {
          handleFocus();
        },
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldPlaceholder, fieldReadonly, full, minimal, veryMinimal, props.minHeight, props.theme, autoFocus, onAgent, autoCompleteSettings]);

  return (
    <FormGroup {...props.groupProps}>
      <Label for={formControl.uid}>{fieldConfig.label}</Label>
      {tooltip}
      <MFormControl error={!!fieldError} fullWidth>
        <JoditEditor ref={joditRef} config={config} onBlur={handleBlur} onChange={handleChangeText} value={initialValue} />
      </MFormControl>
      {props?.note && <div className="form-text text-muted mt-2">{props.note}</div>}
      {!!fieldError && (
        <FormHelperText className="mui-error" style={{ color: '#f44336' }}>
          {fieldError}
        </FormHelperText>
      )}
    </FormGroup>
  );
}
